'use client';
import { factory, Flex, AspectRatioKeeper, Image, ColorSwatchRadioGroup, Typography, DsmColor, omitText, Button } from '@kohler-dsm-library/kohler-core';
import { CheckmarkIcon } from '@kohler-dsm-library/kohler-icons';
import clsx from 'clsx';
import React, { useState, useMemo, isValidElement } from 'react';
import classes from './ExperienceCompareCard.module.css.mjs';

const ExperienceCompareCard = factory((props, ref) => {
  const { CTA, title, description, price, image, imagesByColor, features, colorSwatches, className, ...rest } = props;
  const isControlled = colorSwatches?.value !== void 0;
  const [internalColor, setInternalColor] = useState(colorSwatches?.defaultValue);
  const selectedColor = isControlled ? colorSwatches?.value : internalColor;
  const handleColorChange = (value) => {
    if (!isControlled) setInternalColor(value);
    colorSwatches?.onChange?.(value);
  };
  const resolvedImage = useMemo(() => {
    if (!selectedColor) return image;
    const byMap = imagesByColor?.[selectedColor];
    if (byMap) return byMap;
    return image;
  }, [selectedColor, imagesByColor, colorSwatches?.colors, image]);
  return /* @__PURE__ */ React.createElement(
    Flex,
    {
      direction: "column",
      className: clsx(classes.experienceCompareCardContainer, className, "kohler-experience-compare-card-root"),
      component: "div",
      height: "100%",
      ref,
      ...rest
    },
    /* @__PURE__ */ React.createElement(AspectRatioKeeper, { aspectRatio: "1:1" }, /* @__PURE__ */ React.createElement(Image, { ...resolvedImage })),
    /* @__PURE__ */ React.createElement(Flex, { direction: "column", className: classes.experienceCompareCardContentWrapper }, /* @__PURE__ */ React.createElement(Flex, { direction: "column", gap: 25 }, colorSwatches?.colors?.length ? /* @__PURE__ */ React.createElement(
      ColorSwatchRadioGroup,
      {
        radioGroupClassname: classes.experienceCompareCardColorSwatches,
        "aria-label": `Choose color for ${title?.text ?? "selection"}`,
        ...colorSwatches,
        value: isControlled ? colorSwatches.value : selectedColor,
        onChange: handleColorChange,
        direction: "row"
      }
    ) : null, /* @__PURE__ */ React.createElement(Flex, { direction: "column", gap: 15 }, /* @__PURE__ */ React.createElement(Flex, { direction: "column", gap: 5 }, /* @__PURE__ */ React.createElement(
      Typography,
      {
        component: title.component || "h3",
        ...omitText(title),
        size: "label-regular-l",
        textColor: DsmColor.ContentPrimary
      },
      title.text
    ), description && /* @__PURE__ */ React.createElement(
      Typography,
      {
        component: isValidElement(description.text) ? "div" : description.component || "p",
        ...omitText(description),
        textColor: DsmColor.ContentSecondary,
        size: "paragraph-medium"
      },
      description.text
    )), price && /* @__PURE__ */ React.createElement(
      Typography,
      {
        component: isValidElement(price.text) ? "div" : price.component || "p",
        ...omitText(price),
        textColor: DsmColor.ContentSecondary,
        size: "label-medium-m"
      },
      price.text
    ))), features && features.length > 0 && /* @__PURE__ */ React.createElement(Flex, { component: "ul", direction: "column", margin: 0, className: classes.experienceCompareCardFeaturesContainer }, features.map(({ icon, ...textProps }, index) => /* @__PURE__ */ React.createElement(Flex, { key: `feature-${index}`, gap: 10, align: "center", component: "li", textColor: DsmColor.ContentSecondary }, icon ?? /* @__PURE__ */ React.createElement(CheckmarkIcon, { size: "small", withBoundingBox: true }), /* @__PURE__ */ React.createElement(
      Typography,
      {
        component: isValidElement(textProps.text) ? "div" : textProps.component || "p",
        ...omitText(textProps),
        textColor: DsmColor.ContentSecondary,
        size: "label-regular-m"
      },
      textProps.text
    )))), CTA && /* @__PURE__ */ React.createElement(Button, { component: "a", marginTop: "auto", variant: "secondary", size: "medium", fullWidth: true, ...omitText(CTA) }, CTA.text))
  );
});

export { ExperienceCompareCard };
